import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';
import axios from 'axios';

// Create the context
const BookingDetailsContext = createContext();

export const useBookingDetails = () => useContext(BookingDetailsContext);

export const BookingDetailsProvider = ({ children }) => {
  // Single booking details state
  const [currentBooking, setCurrentBooking] = useState(null);
  const [bookingActivities, setBookingActivities] = useState([]);
  const [bookingNotes, setBookingNotes] = useState([]);
  const [bookingPayments, setBookingPayments] = useState([]);
  const [bookingDocuments, setBookingDocuments] = useState([]);
  const [bookingCommissions, setBookingCommissions] = useState([]);
  const [bookingCommissionPayments, setBookingCommissionPayments] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingAgentIncentives, setBookingAgentIncentives] = useState([]);
  const [error, setError] = useState(null);

  // Single booking operations
  const fetchBookingDetails = useCallback(async (bookingId) => {
    setBookingLoading(true);
    setError(null);
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/`);
      setCurrentBooking(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking details:', error);
      const errorMessage = error.response?.status === 500 
        ? 'Server error occurred. Please try again later.' 
        : error.message || 'Failed to fetch booking details';
      setError(errorMessage);
      throw error;
    } finally {
      setBookingLoading(false);
    }
  }, []);

  const fetchBookingActivities = useCallback(async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/activities/`);
      setBookingActivities(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking activities:', error);
      throw error;
    }
  }, []);

  const fetchBookingPayments = useCallback(async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/payments/`);
      setBookingPayments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking payments:', error);
      throw error;
    }
  }, []);

  const fetchBookingDocuments = useCallback(async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/documents/`);
      setBookingDocuments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking documents:', error);
      throw error;
    }
  }, []);

  const fetchBookingCommissions = useCallback(async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/commissions/`);
      setBookingCommissions(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking commissions:', error);
      throw error;
    }
  }, []);

  const fetchBookingCommissionPayments = useCallback(async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/commission-payments/`);
      setBookingCommissionPayments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking commission payments:', error);
      throw error;
    }
  }, []);

  // Add operations
  const addBookingPayment = useCallback(async (bookingId, paymentData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/payments/`, paymentData);
      await fetchBookingPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking payment:', error);
      throw error;
    }
  }, [fetchBookingPayments]);

  const addBookingDocument = useCallback(async (bookingId, documentData) => {
    try {
      let response;
      
      if (documentData instanceof FormData) {
        // For FormData, use axios directly with proper configuration
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '/api';
        
        response = await axios.post(`${API_BASE_URL}/bms/bookings/${bookingId}/documents/`, documentData, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            // Don't set Content-Type - let browser set it for FormData
          }
        });
      } else {
        // For regular JSON data, use the api service
        response = await api.post(`/bms/bookings/${bookingId}/documents/`, documentData);
      }
      
      await fetchBookingDocuments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking document:', error);
      throw error;
    }
  }, [fetchBookingDocuments]);

  const addBookingCommission = useCallback(async (bookingId, commissionData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/commissions/`, commissionData);
      await fetchBookingCommissions(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking commission:', error);
      throw error;
    }
  }, [fetchBookingCommissions]);

  const addBookingCommissionPayment = useCallback(async (bookingId, paymentData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/commission-payments/`, paymentData);
      await fetchBookingCommissionPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking commission payment:', error);
      throw error;
    }
  }, [fetchBookingCommissionPayments]);

  // Update operations
  const updateBookingPayment = useCallback(async (bookingId, paymentId, paymentData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/payments/${paymentId}/`, paymentData);
      await fetchBookingPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking payment:', error);
      throw error;
    }
  }, [fetchBookingPayments]);

  const updateBookingDocument = useCallback(async (bookingId, documentId, documentData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/documents/${documentId}/`, documentData);
      await fetchBookingDocuments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking document:', error);
      throw error;
    }
  }, [fetchBookingDocuments]);

  const updateBookingCommission = useCallback(async (bookingId, commissionId, commissionData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/commissions/${commissionId}/`, commissionData);
      await fetchBookingCommissions(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking commission:', error);
      throw error;
    }
  }, [fetchBookingCommissions]);

  const updateBookingCommissionPayment = useCallback(async (bookingId, paymentId, paymentData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/commission-payments/${paymentId}/`, paymentData);
      await fetchBookingCommissionPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking commission payment:', error);
      throw error;
    }
  }, [fetchBookingCommissionPayments]);

  // Delete operations
  const deleteBookingPayment = useCallback(async (bookingId, paymentId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/payments/${paymentId}/`);
      await fetchBookingPayments(bookingId);
    } catch (error) {
      console.error('Error deleting booking payment:', error);
      throw error;
    }
  }, [fetchBookingPayments]);

  const deleteBookingDocument = useCallback(async (bookingId, documentData) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/documents/`, { data: documentData });
      await fetchBookingDocuments(bookingId);
    } catch (error) {
      console.error('Error deleting booking document:', error);
      throw error;
    }
  }, [fetchBookingDocuments]);

  const deleteBookingCommission = useCallback(async (bookingId, commissionId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/commissions/${commissionId}/`);
      await fetchBookingCommissions(bookingId);
    } catch (error) {
      console.error('Error deleting booking commission:', error);
      throw error;
    }
  }, [fetchBookingCommissions]);

  const deleteBookingCommissionPayment = useCallback(async (bookingId, paymentId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/commission-payments/${paymentId}/`);
      await fetchBookingCommissionPayments(bookingId);
    } catch (error) {
      console.error('Error deleting booking commission payment:', error);
      throw error;
    }
  }, [fetchBookingCommissionPayments]);

  // Update booking status
  const updateBookingStatus = useCallback(async (bookingId, newStatus) => {
    try {
      const response = await api.patch(`/bms/bookings/${bookingId}/update_status/`, { new_status: newStatus });
      
      // Update current booking if it's the one being modified
      if (currentBooking?.id === bookingId) {
        setCurrentBooking(prevBooking => ({
          ...prevBooking,
          status: newStatus,
          status_verbose: response.data.status_verbose
        }));
      }

      return response.data;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  }, [currentBooking]);

  // Update booking details
  const updateBooking = useCallback(async (bookingId, bookingData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/`, bookingData);
      
      // Update current booking if it's the one being modified
      if (currentBooking?.id === bookingId) {
        setCurrentBooking(response.data);
      }
      
      return response
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  }, [currentBooking]);

  // Assign project and unit to booking (dedicated endpoint for better modularity)
  const assignProjectUnit = useCallback(async (bookingId, projectId, unitId = null) => {
    try {
      const payload = {};
      if (projectId) {
        payload.project = projectId;
      }
      if (unitId) {
        payload.unit = unitId;
      }
      
      const response = await api.patch(`/bms/bookings/${bookingId}/assign-project-unit/`, payload);
      
      // Update current booking if it's the one being modified
      if (currentBooking?.id === bookingId) {
        setCurrentBooking(response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error assigning project/unit:', error);
      throw error;
    }
  }, [currentBooking]);



  // Other customer operations
  const updateAdditionalCustomer = useCallback(async (bookingId, customerData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/additional-customers/`, {'additional_customers': customerData});
      await fetchBookingDetails(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating additional customer:', error);
      throw error;
    }
  }, [fetchBookingDetails]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/bms/bookings/dashboard/');
      return response;
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      throw err;
    }
  };

  const updatePrimaryCustomer = useCallback(async (bookingId, customerData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/primary-customer/`, {'primary_customer': customerData});
      await fetchBookingDetails(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating primary customer:', error);
      throw error;
    }
  }, [fetchBookingDetails]);

  const updateBookingDates = useCallback(async (bookingId, dateType, dateValue) => {
    try {
      const response = await api.patch(`/bms/bookings/${bookingId}/update-dates/${dateType}/`, { date_value: dateValue });
      await fetchBookingDetails(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking dates:', error);
      throw error;
    }
  }, [fetchBookingDetails]);


  const fetchBookingAgentIncentives = useCallback(async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/agent-incentives/`);
      setBookingAgentIncentives(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking agent incentives:', error);
      throw error;
    }
  }, []);


  const addBookingAgentIncentive = useCallback(async (bookingId, incentiveData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/agent-incentives/`, incentiveData);
      await fetchBookingAgentIncentives(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking agent incentive:', error);
      throw error;
    }
  }, [fetchBookingAgentIncentives]);

  const updateBookingAgentIncentive = useCallback(async (bookingId, incentiveId, incentiveData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/agent-incentives/${incentiveId}/`, incentiveData);
      await fetchBookingAgentIncentives(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking agent incentive:', error);
      throw error;
    }
  }, [fetchBookingAgentIncentives]);

  const deleteBookingAgentIncentive = useCallback(async (bookingId, incentiveId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/agent-incentives/${incentiveId}/`);
      await fetchBookingAgentIncentives(bookingId);
    } catch (error) {
      console.error('Error deleting booking agent incentive:', error);
      throw error;
    }
  }, [fetchBookingAgentIncentives]);

  const attachProof = useCallback(async (bookingId, proofType, paymentId, file) => {
    try {
      const formData = new FormData();
      formData.append('proof_type', proofType);
      formData.append('payment_id', paymentId);
      formData.append('file', file);

      const response = await api.post(`/bms/bookings/${bookingId}/attach_proof/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refresh the relevant data based on proof type
      if (proofType === 'booking') {
        await fetchBookingPayments(bookingId);
      } else if (proofType === 'commission') {
        await fetchBookingCommissionPayments(bookingId);
      }

      return response.data;
    } catch (error) {
      console.error('Error attaching proof:', error);
      throw error;
    }
  }, [fetchBookingPayments, fetchBookingCommissionPayments]);

  // Clear booking details (useful when navigating away)
  const clearBookingDetails = useCallback(() => {
    setCurrentBooking(null);
    setBookingActivities([]);
    setBookingNotes([]);
    setBookingPayments([]);
    setBookingDocuments([]);
    setBookingCommissions([]);
    setBookingCommissionPayments([]);
    setBookingAgentIncentives([]);
    setError(null);
  }, []);

  // Context value
  const value = {
    // States
    currentBooking,
    bookingActivities,
    bookingNotes,
    bookingPayments,
    bookingDocuments,
    bookingCommissions,
    bookingCommissionPayments,
    bookingAgentIncentives,
    bookingLoading,
    error,

    // Single booking operations
    fetchBookingDetails,
    fetchBookingActivities,
    fetchBookingPayments,
    fetchBookingDocuments,
    fetchBookingCommissions,
    fetchBookingCommissionPayments,
    fetchBookingAgentIncentives,
    fetchDashboardData,

    // Add operations
    addBookingPayment,
    addBookingDocument,
    addBookingCommission,
    addBookingCommissionPayment,
    addBookingAgentIncentive,

    // Update operations
    updateBooking,
    assignProjectUnit,
    updateBookingPayment,
    updateBookingDocument,
    updateBookingCommission,
    updateBookingCommissionPayment,
    updateBookingAgentIncentive,
    updateBookingStatus,

    // Delete operations
    deleteBookingPayment,
    deleteBookingDocument,
    deleteBookingCommission,
    deleteBookingCommissionPayment,
    deleteBookingAgentIncentive,

    // Other operations
    updateAdditionalCustomer,
    updatePrimaryCustomer,
    clearBookingDetails,
    updateBookingDates,
    attachProof,
  };

  return <BookingDetailsContext.Provider value={value}>{children}</BookingDetailsContext.Provider>;
};
